import RouteTemplate from "ember-route-template";
import { on } from "@ember/modifier";
import { fn, concat } from "@ember/helper";
import eq from "truth-helpers/helpers/eq";
import not from "truth-helpers/helpers/not";
import lte from "truth-helpers/helpers/lte";
import gte from "truth-helpers/helpers/gte";
export default RouteTemplate(
  <template>
    <div class="proxyviews-admin-dashboard">
      <div class="pd-admin-header">
        <div class="header-title-wrapper">
          <div>
            <h2>Benchmarks</h2>
            <p class="description">Proxy provider performance test results and user alert management.</p>
          </div>
        </div>
      </div>

      {{#if @controller.message}}
        <div class="pd-toast show {{if (eq @controller.message.type 'danger') 'error'}}">
          <span class="pd-toast__text">{{@controller.message.text}}</span>
          <button type="button" class="pd-toast__dismiss" aria-label="Dismiss" {{on "click" @controller.dismissMessage}}>&times;</button>
        </div>
      {{/if}}

      {{#if @controller.stats}}
        <div class="pd-admin-stats">
          <div class="pd-admin-stat-card">
            <div class="stat-header"><span class="label">Total Runs</span></div>
            <span class="value">{{@controller.stats.total_runs}}</span>
          </div>
          <div class="pd-admin-stat-card">
            <div class="stat-header"><span class="label">Last 24h</span></div>
            <span class="value">{{@controller.stats.runs_last_24h}}</span>
          </div>
          <div class="pd-admin-stat-card">
            <div class="stat-header"><span class="label">Providers Tested</span></div>
            <span class="value">{{@controller.stats.providers_tested}}</span>
          </div>
          <div class="pd-admin-stat-card">
            <div class="stat-header"><span class="label">Avg Success</span></div>
            <span class="value">{{@controller.stats.avg_success_rate}}%</span>
          </div>
        </div>
      {{/if}}

      <div class="pd-admin-tabs">
        <button type="button" class="pd-admin-tab-btn {{if (eq @controller.activeSection 'runs') 'active'}}" {{on "click" (fn @controller.setSection 'runs')}}>Runs</button>
        <button type="button" class="pd-admin-tab-btn {{if (eq @controller.activeSection 'alerts') 'active'}}" {{on "click" (fn @controller.setSection 'alerts')}}>Alerts</button>
      </div>

      {{#if (eq @controller.activeSection 'runs')}}
      <div class="pd-admin-filter-bar">
        <input type="text" placeholder="Search by provider..." value={{@controller.runSearch}} class="pd-admin-search-input" {{on "input" @controller.updateRunSearch}} />
        <button type="button" class="btn btn-default btn-small" {{on "click" @controller.toggleNewRunForm}}>
          {{if @controller.showNewRunForm "Cancel" "+ New Benchmark Run"}}
        </button>
        <a href="/admin/plugins/proxyviews-benchmarks/runs/export.csv" class="btn btn-default btn-small">Export CSV</a>
        {{#if @controller.loading}}
          <span class="pd-admin-search-status">Loading…</span>
        {{/if}}
      </div>

      {{#if @controller.showNewRunForm}}
        <div class="calculator-sections-container pd-admin-form-panel" {{on "keydown" @controller.handleFormKeydown}}>
          <div class="pd-admin-form-panel__header">
            <h3>New Benchmark Run</h3>
            <span class="pd-admin-form-panel__hint">Press Escape to cancel</span>
          </div>
          <div class="pd-admin-new-provider-form pd-admin-form-grid">
            <div class="pd-admin-form-section">
              <span class="pd-admin-form-section__label">Provider & Test Config</span>
            </div>
            <div class="form-grid-row">
              <div class="pd-admin-field">
                <label class="pd-admin-field__label">Provider <span class="pd-admin-required">*</span></label>
                <select {{on "change" (fn @controller.updateNewRunField "provider_id")}}>
                  <option value="">Select provider…</option>
                  {{#each @controller.allProviders as |p|}}
                    <option value={{p.id}} selected={{eq @controller.newRun.provider_id p.id}}>{{p.name}}</option>
                  {{/each}}
                </select>
              </div>
              <div class="pd-admin-field">
                <label class="pd-admin-field__label">Test Type</label>
                <select {{on "change" (fn @controller.updateNewRunField "test_type")}}>
                  {{#each @controller.testTypes as |t|}}
                    <option value={{t}} selected={{eq @controller.newRun.test_type t}}>{{t}}</option>
                  {{/each}}
                </select>
              </div>
            </div>
            <div class="form-grid-row">
              <div class="pd-admin-field">
                <label class="pd-admin-field__label">Region</label>
                <select {{on "change" (fn @controller.updateNewRunField "region")}}>
                  {{#each @controller.regions as |r|}}
                    <option value={{r}} selected={{eq @controller.newRun.region r}}>{{r}}</option>
                  {{/each}}
                </select>
              </div>
              <div class="pd-admin-field">
                <label class="pd-admin-field__label">Protocol</label>
                <select {{on "change" (fn @controller.updateNewRunField "protocol")}}>
                  {{#each @controller.protocols as |pr|}}
                    <option value={{pr}} selected={{eq @controller.newRun.protocol pr}}>{{pr}}</option>
                  {{/each}}
                </select>
              </div>
            </div>

            <div class="pd-admin-form-section">
              <span class="pd-admin-form-section__label">Results</span>
            </div>
            <div class="form-grid-row">
              <div class="pd-admin-field">
                <label class="pd-admin-field__label">Concurrent Connections</label>
                <input type="number" min="1" value={{@controller.newRun.concurrent_connections}} class="input-small" {{on "input" (fn @controller.updateNewRunField "concurrent_connections")}} />
              </div>
              <div class="pd-admin-field">
                <label class="pd-admin-field__label">Total Requests</label>
                <input type="number" min="0" value={{@controller.newRun.total_requests}} class="input-small" {{on "input" (fn @controller.updateNewRunField "total_requests")}} />
              </div>
              <div class="pd-admin-field">
                <label class="pd-admin-field__label">Successful</label>
                <input type="number" min="0" value={{@controller.newRun.successful_requests}} class="input-small" {{on "input" (fn @controller.updateNewRunField "successful_requests")}} />
              </div>
              <div class="pd-admin-field">
                <label class="pd-admin-field__label">Avg Response (ms)</label>
                <input type="number" step="0.01" value={{@controller.newRun.avg_response_time_ms}} class="input-small" {{on "input" (fn @controller.updateNewRunField "avg_response_time_ms")}} />
              </div>
            </div>
            <div class="pd-admin-form-actions">
              <button type="button" class="btn btn-primary btn-small {{if (eq @controller.savingId 'new-run') 'disabled'}}" {{on "click" @controller.createRun}}>
                {{#if (eq @controller.savingId 'new-run')}}<span class="pd-admin-btn-spinner"></span> Creating...{{else}}Create Run{{/if}}
              </button>
              <button type="button" class="btn btn-default btn-small" {{on "click" @controller.toggleNewRunForm}}>Cancel</button>
            </div>
          </div>
        </div>
      {{/if}}

      <div class="pd-admin-table-container">
        <table class="pd-admin-table">
          <thead>
            <tr>
              <th scope="col">Provider</th>
              <th scope="col">Type</th>
              <th scope="col">Region</th>
              <th scope="col">Protocol</th>
              <th scope="col">Conns</th>
              <th scope="col">Requests</th>
              <th scope="col">Success</th>
              <th scope="col">Avg ms</th>
              <th scope="col">Success Rate</th>
              <th scope="col">Grade</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {{#each @controller.filteredRuns as |r|}}
              <tr class="{{if (eq @controller.editingRunId r.id) 'pd-row-editing'}}">
                <td><strong>{{r.provider_name}}</strong> <span class="slug">({{r.provider_slug}})</span></td>
                <td>{{r.test_type}}</td>
                <td>{{r.region}}</td>
                <td>{{r.protocol}}</td>
                <td>{{r.concurrent_connections}}</td>
                <td>{{r.total_requests}}</td>
                <td>{{r.successful_requests}}</td>
                <td>{{r.avg_response_time_ms}}</td>
                <td>{{r.success_rate}}%</td>
                <td><span class="badge">{{r.performance_grade}}</span></td>
                <td class="pd-admin-actions-cell">
                  <button type="button" class="btn btn-default btn-small" {{on "click" (fn @controller.startEditRun r)}}>
                    {{if (eq @controller.editingRunId r.id) "Editing..." "Edit"}}
                  </button>
                  <button type="button" class="btn btn-danger btn-small" {{on "click" (fn @controller.deleteRun r)}}>
                    Delete
                  </button>
                </td>
              </tr>

              {{#if (eq @controller.editingRunId r.id)}}
                <tr class="pd-edit-row">
                  <td colspan="11">
                    <div class="pd-edit-row__panel">
                      <h4>Edit Run #{{r.id}} — {{r.provider_name}}</h4>
                      <div class="pd-edit-row__fields">
                        <div class="pd-edit-row__field">
                          <label class="pd-sidebar-label">Type</label>
                          <select {{on "change" (fn @controller.updateRunDraft "test_type")}}>
                            {{#each @controller.testTypes as |t|}}
                              <option value={{t}} selected={{eq @controller.runDraft.test_type t}}>{{t}}</option>
                            {{/each}}
                          </select>
                        </div>
                        <div class="pd-edit-row__field">
                          <label class="pd-sidebar-label">Region</label>
                          <select {{on "change" (fn @controller.updateRunDraft "region")}}>
                            {{#each @controller.regions as |reg|}}
                              <option value={{reg}} selected={{eq @controller.runDraft.region reg}}>{{reg}}</option>
                            {{/each}}
                          </select>
                        </div>
                        <div class="pd-edit-row__field">
                          <label class="pd-sidebar-label">Protocol</label>
                          <select {{on "change" (fn @controller.updateRunDraft "protocol")}}>
                            {{#each @controller.protocols as |pr|}}
                              <option value={{pr}} selected={{eq @controller.runDraft.protocol pr}}>{{pr}}</option>
                            {{/each}}
                          </select>
                        </div>
                        <div class="pd-edit-row__field">
                          <label class="pd-sidebar-label">Connections</label>
                          <input type="number" min="1" value={{@controller.runDraft.concurrent_connections}} {{on "input" (fn @controller.updateRunDraft "concurrent_connections")}} class="input-small" />
                        </div>
                        <div class="pd-edit-row__field">
                          <label class="pd-sidebar-label">Total Requests</label>
                          <input type="number" min="0" value={{@controller.runDraft.total_requests}} {{on "input" (fn @controller.updateRunDraft "total_requests")}} class="input-small" />
                        </div>
                        <div class="pd-edit-row__field">
                          <label class="pd-sidebar-label">Successful</label>
                          <input type="number" min="0" value={{@controller.runDraft.successful_requests}} {{on "input" (fn @controller.updateRunDraft "successful_requests")}} class="input-small" />
                        </div>
                        <div class="pd-edit-row__field">
                          <label class="pd-sidebar-label">Avg Response (ms)</label>
                          <input type="number" step="0.01" value={{@controller.runDraft.avg_response_time_ms}} {{on "input" (fn @controller.updateRunDraft "avg_response_time_ms")}} class="input-small" />
                        </div>
                      </div>
                      <div class="pd-edit-row__actions">
                        <button type="button" class="btn btn-default btn-small" {{on "click" @controller.cancelEditRun}}>Cancel</button>
                        <button type="button" class="btn btn-primary btn-small {{if (eq @controller.savingId (concat 'run-' r.id)) 'disabled'}}" {{on "click" (fn @controller.saveRun r)}}>
                          {{#if (eq @controller.savingId (concat 'run-' r.id))}}<span class="pd-admin-btn-spinner"></span> Saving...{{else}}Save Changes{{/if}}
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              {{/if}}
             {{else}}
              <tr>
                <td colspan="11">
                  <div class="pd-empty-state">
                    <div class="pd-empty__icon">&#9889;</div>
                    <h3 class="pd-empty__title">No Benchmark Runs Yet</h3>
                    <p class="pd-empty__description">Click "+ New Benchmark Run" to add your first performance test.</p>
                    <button type="button" class="btn btn-primary pd-empty__action" {{on "click" @controller.toggleNewRunForm}}>
                      Create First Run
                    </button>
                  </div>
                </td>
              </tr>
             {{/each}}
          </tbody>
        </table>
      </div>

      {{#unless (eq @controller.totalPages 1)}}
        <nav class="pd-admin-pagination" aria-label="Benchmark runs pages">
          <button type="button" class="btn btn-default btn-small pd-page-btn" disabled={{not @controller.hasPrevPage}} aria-label="Previous page" {{on "click" @controller.prevPage}}>&larr; Previous</button>
          <span class="pd-admin-pagination__status">Page {{@controller.page}} of {{@controller.totalPages}} ({{@controller.meta.total_count}} runs)</span>
          <button type="button" class="btn btn-default btn-small pd-page-btn" disabled={{not @controller.hasNextPage}} aria-label="Next page" {{on "click" @controller.nextPage}}>Next &rarr;</button>
        </nav>
      {{/unless}}
      {{/if}}

      {{#if (eq @controller.activeSection 'alerts')}}
        <div class="pd-admin-filter-bar" style="margin-bottom:8px;">
          <button
            type="button"
            class="btn btn-default btn-small {{if (eq @controller.savingId 'purge-stale') 'disabled'}}"
            {{on "click" @controller.purgeStaleAlerts}}
          >
            {{#if (eq @controller.savingId 'purge-stale')}}<span class="pd-admin-btn-spinner"></span> Purging...{{else}}Purge Stale (inactive 90+ days){{/if}}
          </button>
        </div>
        {{#if @controller.alertsLoading}}<div class="pd-admin-search-status">Loading…</div>{{/if}}

        <div class="pd-admin-table-container">
          <table class="pd-admin-table">
            <thead>
              <tr>
                <th scope="col">Provider</th>
                <th scope="col">Owner</th>
                <th scope="col">Condition</th>
                <th scope="col">Threshold</th>
                <th scope="col">Active</th>
                <th scope="col">Last Triggered</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {{#each @controller.alerts as |a|}}
                <tr>
                  <td><strong>{{#if a.provider}}{{a.provider.name}}{{else}}—{{/if}}</strong></td>
                  <td>{{#if a.user}}{{a.user.username}}{{else}}—{{/if}}</td>
                  <td><span class="badge badge-type">{{a.human_condition}}</span></td>
                  <td>
                    <input type="number" step="0.01" value={{a.threshold}} class="input-small" {{on "input" (fn @controller.updateAlertThreshold a)}} />
                    <button type="button" class="btn btn-default btn-small" disabled={{eq @controller.savingId (concat 'alert-save-' a.id)}} {{on "click" (fn @controller.saveAlertThreshold a)}}>Set</button>
                  </td>
                  <td>
                    <label class="pd-switch">
                      <input type="checkbox" checked={{a.active}} disabled={{eq @controller.savingId (concat 'alert-toggle-' a.id)}} {{on "change" (fn @controller.toggleAlertActive a)}} />
                      <span class="pd-slider"></span>
                    </label>
                  </td>
                  <td>{{#if a.last_triggered_at}}{{a.last_triggered_at}}{{else}}<span class="muted">never</span>{{/if}}</td>
                  <td class="pd-admin-actions-cell">
                    <button type="button" class="btn btn-danger btn-small {{if (eq @controller.savingId (concat 'alert-delete-' a.id)) 'disabled'}}" {{on "click" (fn @controller.deleteAlert a)}}>Delete</button>
                  </td>
                </tr>
                {{else}}
                  <tr>
                    <td colspan="7">
                      <div class="pd-empty-state">
                        <div class="pd-empty__icon">&#128276;</div>
                        <h3 class="pd-empty__title">No Alerts Yet</h3>
                        <p class="pd-empty__description">Users can configure performance alerts from their dashboard.</p>
                      </div>
                    </td>
                  </tr>
                {{/each}}
            </tbody>
          </table>
        </div>

        {{#unless (eq @controller.alertsMeta.total_pages 1)}}
          <nav class="pd-admin-pagination" aria-label="Benchmark alerts pages">
            <button type="button" class="btn btn-default btn-small pd-page-btn" disabled={{lte @controller.alertsPage 1}} aria-label="Previous page" {{on "click" @controller.prevAlertsPage}}>&larr; Previous</button>
            <span class="pd-admin-pagination__status">Page {{@controller.alertsPage}} of {{@controller.alertsMeta.total_pages}} ({{@controller.alertsMeta.total_count}} alerts)</span>
            <button type="button" class="btn btn-default btn-small pd-page-btn" disabled={{gte @controller.alertsPage @controller.alertsMeta.total_pages}} aria-label="Next page" {{on "click" @controller.nextAlertsPage}}>Next &rarr;</button>
          </nav>
        {{/unless}}
      {{/if}}
    </div>

  </template>
);
